/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 */

import { addMessages } from '../messages';
import { publicConstants } from '../constants';
import fieldDefaultUtils from '../fields/field-default';
import type { ComponentMetadataConfig } from '../interpreter/types';
import FieldUtils from '../fields';
import AttachmentUtils from '../attachment/index';
import type { NodeProps } from '../contextree/types';
import type { AttachmentData, AttachmentObject, AttachmentValidate } from '../attachment/types';

/**
 * @description
 * Provides the basic APIs to handle validation in the Constellation infrastructure
 */
class Validator {
  private validators: { [key: string]: Function };

  constructor() {
    this.validators = {};
  }

  static messageDispatcher(
    prop?: string,
    pageReference?: string,
    context?: string,
    message?: string,
    fieldLabel?: string,
    options?: { subType: string }
  ) {
    if (message) {
      const messages: { type: string; fieldLabel?: string; message: string }[] = [];
      messages.push({
        type: publicConstants.MESSAGES.MESSAGES_TYPE_ERROR,
        fieldLabel,
        message,
        ...(options ?? {})
      });
      addMessages({
        messages,
        property: prop,
        pageReference,
        context
      });
    }
  }

  /**
   * Registers the validator function to the validators object which holds all the validator functions.
   * @param name name of the validation like email etc.,
   * @param validatorFn validator function which contains the code logic to perform validation.
   *
   * @example <caption>Example for register()</caption>
   * Validator.register('isAlpha', () => { // implementation code goes here..  // })
   * 'This example will register the isAlpha validator function in validators object with key as "isAlpha" and value as second parameter passed to this function'
   */
  register(name: string, validatorFn: Function) {
    this.validators[name] = validatorFn;
  }

  /**
   * Returns the validator associated with validation type.
   * @param name name of the validation.
   * @returns returns the validator
   *
   * @example <caption>Example for getValidator()</caption>
   * Validator.getValidator('urgencyvalue');
   * 'example usage returns the urgencyvalue validator function'.
   */
  getValidator(name: string) {
    return this.validators[name];
  }

  /**
   * Performs required validation and returns the validation message
   * @param name validation type
   * @param prop name
   * @param pageReference name
   * @param value value to be validated
   * @param context context name
   * @param c11nEnv Constellation env object.
   *
   * @example <caption>Example for validateRequired()</caption>
   * Validator.validateRequired('radiobuttons', 'gender', 'male', 'app/primary_3', '[...] c11nEnv object');
   * `above example shows the usage of required validation on radiobuttons control with gender property under app/primary_3 context`.
   */
  validateRequired(
    name: string | undefined,
    prop: string | undefined,
    pageReference: string | undefined,
    value: string | AttachmentValidate,
    context: string | undefined,
    contextTreeNodeProps: NodeProps
  ) {
    const { required, value: contextTreeNodeValue } = contextTreeNodeProps;
    let message: string | null = null;
    /* Required val */
    const isRequired = !!(required === 'true' || required === true);

    if (isRequired) {
      let newValue = value;

      switch (name) {
        case 'radiobuttons':
          newValue = contextTreeNodeValue as string;
          break;
        case 'attachment': {
          newValue = AttachmentUtils.validateAttachments(
            value as AttachmentValidate,
            contextTreeNodeValue as AttachmentData | AttachmentObject
          );
          break;
        }
        case 'checkbox':
          if (!newValue || newValue === 'false') {
            newValue = '';
          }
          break;
        default:
          if (typeof newValue === 'string') {
            newValue = newValue.trim();
          }
      }
      message = this.getValidator('required')(newValue) as string;
      Validator.messageDispatcher(
        prop,
        pageReference,
        context,
        message,
        FieldUtils.getFieldLabel(contextTreeNodeProps),
        { subType: 'required' }
      );
    }
    return message;
  }

  /**
   * This Function helps to perform validation based on the validator name passed in first argument and
   * returns validation message.
   * @param name validation type e.g. email, integer etc.
   * @param prop name of the property bound to the component.
   * @param pageReference - page reference of the property
   * @param value value to be validated
   * @param context context name
   * @param c11nEnv Constellation env object of the component.
   *
   * @example <caption>Example for validateType()</caption>
   * Validator.validateType('email', 'businessEmail', 'abc@xyz.com', 'app/primary_4', '[...] c11nEnv object');
   * `above example shows the usage of email type validation on businessEmail property under app/primary_4 context`.
   */
  validateType(
    name: string | undefined,
    prop: string | undefined,
    pageReference: string | undefined,
    value: string | AttachmentValidate,
    context: string | undefined,
    contextTreeNodeProps: NodeProps
  ) {
    let message: string | null = null;
    const validatorOptions = {
      pageReference,
      context,
      property: prop,
      target: contextTreeNodeProps?.target,
      required: contextTreeNodeProps?.required
    };
    let isClientValidationNeeded = fieldDefaultUtils.getDefaultsforType(contextTreeNodeProps?.componentName || '')?.[
      'client-validations'
    ];
    if (isClientValidationNeeded !== false && name && this.getValidator(name)) {
      message = this.getValidator(name)(value, validatorOptions) as string;
      if (
        value !== '' ||
        fieldDefaultUtils.getDefaultsforType(contextTreeNodeProps?.componentName || '')?.showErrorWhenEmpty
      ) {
        Validator.messageDispatcher(prop, pageReference, context, message, contextTreeNodeProps?.label);
      }
    }

    // OOTB Clientside Validations
    const validationType = contextTreeNodeProps?.validationType;
    isClientValidationNeeded =
      validationType && fieldDefaultUtils.getDefaultsforType(validationType)?.['client-validations'];
    if (isClientValidationNeeded !== false && validationType && this.getValidator(validationType)) {
      message = this.getValidator(validationType)(value, validatorOptions) as string;
      Validator.messageDispatcher(prop, pageReference, context, message, contextTreeNodeProps?.label);
    }

    return message;
  }

  /**
   * Validates the field and returns the message if
   * validation fails
   * @param name validation type
   * @param prop name
   * @param value value to be validated
   * @param c11nEnv Constellation env object.
   *
   * @example <caption>Example for validate()</caption>
   * Validator.validate('integer', 'numberOfEmployees', 'abc@xyz.com', '[...] c11nEnv object');
   * `above example shows the usage of integer type validation on numberOfEmployees property.
   * Here the value passed is a string contains alphabet characters not an integer,
   * hence the validate api sets the "Invalid Number" error message in redux store`.
   */
  validate(
    name: string | undefined,
    prop: string | undefined,
    value: string | AttachmentValidate,
    contextTreeNodeProps: NodeProps = {}
  ) {
    const { context, disabled, readOnly, pageReference, skipRequiredValidation } = contextTreeNodeProps;
    let message: string | null = null;
    if (!disabled && !readOnly) {
      /* Required validation */
      if (!skipRequiredValidation) {
        message = this.validateRequired(name, prop, pageReference, value, context, contextTreeNodeProps);
      }
      /* Type validation after required validation */

      if (!message) {
        message = this.validateType(name, prop, pageReference, value, context, contextTreeNodeProps);
      }
    }
    return { message };
  }

  /**
   * Checks the metadata for any validator is
   * configured or not.
   * @param metadata component metadata
   * @returns returns the boolean.
   *
   * @example <caption>Example for isValidatorExist()</caption>
   * Validator.isValidatorExist({ "validatetype":  "required", 'other': 'properties...' });
   * 'we check for the validatetype property from metadata object. if validatetype key presents then consider as validator configured.'
   *
   */

  isValidatorExist(metadata: NonNullable<ComponentMetadataConfig['config']>) {
    return !!metadata.validatetype;
  }
}

export default new Validator();
